import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Star, MapPin, Calendar, Quote, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import axios from 'axios';

const ReviewsPage = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const res = await axios.get('/api/reviews');
        setReviews(res.data);
      } catch (err) {
        console.error('Failed to fetch reviews:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchReviews();
  }, []);

  const renderStars = (rating) => {
    return [...Array(5)].map((_, index) => (
      <Star key={index} className={`h-5 w-5 ${index < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} />
    ));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString + 'T12:00:00');
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const averageRating = reviews.length > 0 ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length : 5;
  const totalReviews = reviews.length;

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-50 to-gray-100 py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-6">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900">
              Customer <span className="text-red-600">Reviews</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Don't just take our word for it. See what our satisfied customers have to say about their experience with Texas Tint Studio.
            </p>

            {/* Rating Summary */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-8 pt-6 sm:pt-8">
              <div className="text-center">
                <div className="flex items-center justify-center space-x-1 mb-2">
                  {renderStars(Math.round(averageRating))}
                </div>
                <p className="text-3xl font-bold text-gray-900">{averageRating.toFixed(1)}</p>
                <p className="text-gray-600">Average Rating</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-red-600">{totalReviews}+</p>
                <p className="text-gray-600">Happy Customers</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-green-600">100%</p>
                <p className="text-gray-600">Satisfaction Rate</p>
              </div>
            </div>

            {/* Google Reviews Link */}
            <div className="pt-4">
              <a
                href="https://www.google.com/maps/place/Texas+Tint+Studio"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 text-sm text-gray-500 hover:text-red-600 transition-colors"
              >
                <ExternalLink className="h-4 w-4" />
                <span>Leave us a review on Google</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Grid */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Our Customers Say
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
              Real reviews from real customers who trusted us with their window tinting needs.
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading reviews...</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {reviews.map((review) => (
                <Card key={review.id} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <CardContent className="p-6 sm:p-8 space-y-4 sm:space-y-6">
                    <div className="space-y-3 sm:space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-1">{renderStars(review.rating)}</div>
                        <div className="flex items-center space-x-1.5">
                          {review.source === 'google' && (
                            <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium">Google</span>
                          )}
                          {review.source === 'yelp' && (
                            <span className="text-xs bg-red-50 text-red-700 px-2 py-0.5 rounded-full font-medium">Yelp</span>
                          )}
                          <Quote className="h-5 w-5 text-gray-300" />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <h3 className="text-lg font-semibold text-gray-900">{review.name}</h3>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
                          <div className="flex items-center space-x-1">
                            <Calendar className="h-3.5 w-3.5" />
                            <span>{formatDate(review.date)}</span>
                          </div>
                          {review.location && (
                            <div className="flex items-center space-x-1">
                              <MapPin className="h-3.5 w-3.5" />
                              <span>{review.location}</span>
                            </div>
                          )}
                        </div>
                        <div className="inline-block px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                          {review.service}
                        </div>
                      </div>
                    </div>

                    <p className="text-gray-700 leading-relaxed italic text-sm sm:text-base">"{review.review}"</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 text-center">
            <div className="space-y-2">
              <p className="text-3xl sm:text-4xl font-bold text-red-600">500+</p>
              <p className="text-gray-600 font-medium text-sm sm:text-base">Vehicles Tinted</p>
            </div>
            <div className="space-y-2">
              <p className="text-3xl sm:text-4xl font-bold text-red-600">200+</p>
              <p className="text-gray-600 font-medium text-sm sm:text-base">Homes Serviced</p>
            </div>
            <div className="space-y-2">
              <p className="text-3xl sm:text-4xl font-bold text-red-600">5+</p>
              <p className="text-gray-600 font-medium text-sm sm:text-base">Years Experience</p>
            </div>
            <div className="space-y-2">
              <p className="text-3xl sm:text-4xl font-bold text-red-600">100%</p>
              <p className="text-gray-600 font-medium text-sm sm:text-base">Satisfaction</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-red-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6">
            Ready to Join Our Happy Customers?
          </h2>
          <p className="text-lg sm:text-xl text-red-100 mb-6 sm:mb-8 leading-relaxed">
            Experience the quality and service that has earned us excellent reviews.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-red-600 hover:bg-gray-100 px-8 py-4 text-lg rounded-lg transition-all duration-200 hover:shadow-lg">
              <Link to="/book">Book Appointment</Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-2 border-white text-white hover:bg-white hover:text-red-600 px-8 py-4 text-lg rounded-lg transition-all duration-200">
              <a href="tel:(713)446-8928">Call (713) 446-8928</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ReviewsPage;
